import { useLingui } from "@lingui/react/macro";
import { AccessibleIcon } from "@offstreetparking/design-system/accessibility";
import { Alert, toast } from "@offstreetparking/design-system/feedback";
import type { ServerError, ServerValidationException } from "@offstreetparking/design-system/forms";
import { VehicleIcon } from "@offstreetparking/design-system/icons/VehicleIcon";
import { Text } from "@offstreetparking/design-system/typography";
import type { CountryValue } from "@offstreetparking/geography";
import { useState } from "react";
import { offstreetClient } from "../../../../api/client";
import type { RouterOutputs } from "../../../../api/types";
import { useFeatureFlags } from "../../../../hooks/useFeatureFlags";
import { ServerErrorAlert } from "../../../forms/Alert";
import { FlowModalTitle as ModalTitle } from "../../../overlays/FlowModal/FlowModalTitle";
import { ResponsiveOverlay } from "../../../overlays/ResponsiveOverlay";
import { CreateVehicleModal } from "../../../vehicles/CreateVehicleModal";
import { EditVehicleModal } from "../../../vehicles/EditVehicleModal";
import { EditPermitVehiclesForm, type EditPermitVehiclesFormVehicle } from "./EditPermitVehiclesForm";
import { planPermitVehicleChanges } from "./plan-permit-vehicle-changes";

type ParkerVehicle = RouterOutputs["parkers"]["getParkerVehicles"]["items"][number];
type EditPermitVehiclesModalProps = {
	isOpen: boolean;
	onClose: () => void;
	vehicles: {
		id: number;
		plate: string;
		state: string;
	}[];
	publicIdentifier: string;
	publicAccessToken?: string;
	country?: string;
	companyState?: string;
	maxVehicleCount?: number;
	selfServiceVehicleChangesRemaining?: number | null;
	isParker: boolean;
	onVehiclesChanged?: () => Promise<void> | void;
};
export const EditPermitVehiclesModal = ({
	isOpen,
	onClose,
	vehicles,
	publicIdentifier,
	publicAccessToken,
	country,
	companyState,
	maxVehicleCount,
	selfServiceVehicleChangesRemaining,
	isParker,
	onVehiclesChanged,
}: EditPermitVehiclesModalProps) => {
	const { t, i18n } = useLingui();
	const { vehicleChangeLimitFlag } = useFeatureFlags();
	const changesRemaining =
		vehicleChangeLimitFlag && selfServiceVehicleChangesRemaining !== undefined
			? selfServiceVehicleChangesRemaining
			: null;
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isCreateVehicleModalOpen, setIsCreateVehicleModalOpen] = useState(false);
	const [editingVehicle, setEditingVehicle] = useState<ParkerVehicle | undefined>(undefined);
	const isVehicleSubModalOpen = isCreateVehicleModalOpen || !!editingVehicle;
	const utils = offstreetClient.useUtils();
	const { mutateAsync: updatePermitVehicle, error: updatePermitVehicleError } =
		offstreetClient.permit.updatePermitVehiclePublic.useMutation({
			onMutate: async (variables) => {
				await utils.permit.getPermitByPublicIdentifier.cancel({
					publicIdentifier,
				});
				const previousPermit = utils.permit.getPermitByPublicIdentifier.getData({
					publicIdentifier,
				});
				if (previousPermit) {
					utils.permit.getPermitByPublicIdentifier.setData(
						{
							publicIdentifier,
						},
						{
							...previousPermit,
							vehicles: previousPermit.vehicles.map((v) =>
								v.id === variables.vehicle.id
									? {
											...v,
											plate: variables.vehicle.plate,
											state: variables.vehicle.state,
										}
									: v,
							),
						},
					);
				}
				return {
					previousPermit,
				};
			},
			onError: (_err, _variables, context) => {
				if (context?.previousPermit) {
					utils.permit.getPermitByPublicIdentifier.setData(
						{
							publicIdentifier,
						},
						context.previousPermit,
					);
				}
			},
		});
	const { mutateAsync: removePermitVehicle, error: removePermitVehicleError } =
		offstreetClient.permit.removePermitVehiclePublic.useMutation({
			onMutate: async (variables) => {
				await utils.permit.getPermitByPublicIdentifier.cancel({
					publicIdentifier,
				});
				const previousPermit = utils.permit.getPermitByPublicIdentifier.getData({
					publicIdentifier,
				});
				if (previousPermit) {
					utils.permit.getPermitByPublicIdentifier.setData(
						{
							publicIdentifier,
						},
						{
							...previousPermit,
							vehicles: previousPermit.vehicles.filter((v) => v.id !== variables.vehicleId),
						},
					);
				}
				return {
					previousPermit,
				};
			},
			onError: (_err, _variables, context) => {
				if (context?.previousPermit) {
					utils.permit.getPermitByPublicIdentifier.setData(
						{
							publicIdentifier,
						},
						context.previousPermit,
					);
				}
			},
		});
	const { mutateAsync: addPermitVehicle, error: addPermitVehicleError } =
		offstreetClient.permit.addPermitVehiclePublic.useMutation({
			onMutate: async (variables) => {
				await utils.permit.getPermitByPublicIdentifier.cancel({
					publicIdentifier,
				});
				const previousPermit = utils.permit.getPermitByPublicIdentifier.getData({
					publicIdentifier,
				});
				if (previousPermit) {
					utils.permit.getPermitByPublicIdentifier.setData(
						{
							publicIdentifier,
						},
						{
							...previousPermit,
							vehicles: [
								...previousPermit.vehicles,
								{
									id: Date.now(),
									plate: variables.vehicle.plate,
									state: variables.vehicle.state,
								},
							],
						},
					);
				}
				return {
					previousPermit,
				};
			},
			onError: (_err, _variables, context) => {
				if (context?.previousPermit) {
					utils.permit.getPermitByPublicIdentifier.setData(
						{
							publicIdentifier,
						},
						context.previousPermit,
					);
				}
			},
		});

	const serverError = updatePermitVehicleError ?? removePermitVehicleError ?? addPermitVehicleError;
	const removePermitVehicleServerError: ServerError = removePermitVehicleError;
	const removePermitVehicleErrors: ServerValidationException | undefined =
		removePermitVehicleServerError?.data?.errors;
	const removePermitVehicleDescriptor = removePermitVehicleErrors?.fieldErrors?.vehicleId?.[0]?.params?.i18n;
	const removePermitVehicleFriendlyMessage = removePermitVehicleDescriptor
		? i18n._(removePermitVehicleDescriptor)
		: undefined;

	const handleSubmit = async (submittedVehicles: EditPermitVehiclesFormVehicle[]) => {
		setIsSubmitting(true);
		try {
			const { vehiclesToUpdate, vehiclesToRemove, vehiclesToAdd } = planPermitVehicleChanges({
				current: vehicles,
				submitted: submittedVehicles,
			});
			for (const vehicle of vehiclesToUpdate) {
				await updatePermitVehicle({
					vehicle: {
						id: vehicle.id,
						plate: vehicle.plate,
						state: vehicle.state,
					},
					publicIdentifier,
					token: publicAccessToken,
				});
			}
			for (const vehicle of vehiclesToRemove) {
				await removePermitVehicle({
					vehicleId: vehicle.id,
					publicIdentifier,
					token: publicAccessToken,
				});
			}
			for (const vehicle of vehiclesToAdd) {
				await addPermitVehicle({
					vehicle: {
						plate: vehicle.plate,
						state: vehicle.state,
					},
					publicIdentifier,
					token: publicAccessToken,
				});
			}
			const hasChanges = vehiclesToUpdate.length > 0 || vehiclesToRemove.length > 0 || vehiclesToAdd.length > 0;
			if (hasChanges) {
				toast({
					role: "status",
					title: t({
						message: "Vehicle information updated",
						comment: "Success toast after a parker saves changes to the vehicles on their permit",
					}),
				});
			}
			onClose();
		} finally {
			await utils.permit.getPermitByPublicIdentifier.invalidate({ publicIdentifier });
			setIsSubmitting(false);
			await onVehiclesChanged?.();
		}
	};
	return (
		<>
			<ResponsiveOverlay
				isOpen={isOpen && !isVehicleSubModalOpen}
				onOpenChange={(open) => {
					if (!open) {
						onClose();
					}
				}}
				variant={isParker ? "fullScreen" : undefined}
				title={
					isParker ? (
						<ModalTitle
							variant="fullScreen"
							icon={<VehicleIcon className="h-5 w-5" />}
							onClose={onClose}
							i18n={{
								closeButton: t`Close`,
							}}
						>
							{t`Edit Permit Vehicles`}
						</ModalTitle>
					) : (
						<div className="flex items-center gap-2">
							<AccessibleIcon>
								<VehicleIcon className="h-6 w-6" />
							</AccessibleIcon>
							<Text weight="bold">{t`Edit Vehicles`}</Text>
						</div>
					)
				}
			>
				<div className={isParker ? "pb-6" : "border-neutral-faded border-t pt-4"}>
					{removePermitVehicleFriendlyMessage ? (
						<div className="mb-4">
							<Alert type="error" title={removePermitVehicleFriendlyMessage} />
						</div>
					) : (
						<ServerErrorAlert serverError={serverError} />
					)}
					<EditPermitVehiclesForm
						key={vehicles.map((vehicle) => vehicle.id).join()}
						vehicles={vehicles}
						country={country}
						companyState={companyState}
						maxVehicleCount={maxVehicleCount}
						changesRemaining={changesRemaining}
						isSubmitting={isSubmitting}
						isParker={isParker}
						onSubmit={handleSubmit}
						onCreateVehicle={() => setIsCreateVehicleModalOpen(true)}
						onEditVehicle={(vehicle) => setEditingVehicle(vehicle)}
					/>
				</div>
			</ResponsiveOverlay>
			<CreateVehicleModal
				isOpen={isCreateVehicleModalOpen}
				shouldDefault={false}
				country={country as CountryValue | undefined}
				defaultState={companyState}
				onSuccess={async () => {
					await utils.parkers.getParkerVehicles.invalidate();
					setIsCreateVehicleModalOpen(false);
				}}
				onClose={() => setIsCreateVehicleModalOpen(false)}
			/>
			<EditVehicleModal
				isOpen={!!editingVehicle}
				vehicle={editingVehicle}
				country={country as CountryValue | undefined}
				onEdit={async () => {
					await utils.parkers.getParkerVehicles.invalidate();
					setEditingVehicle(undefined);
				}}
				onRemove={async () => {
					await utils.parkers.getParkerVehicles.invalidate();
					setEditingVehicle(undefined);
				}}
				onClose={() => setEditingVehicle(undefined)}
			/>
		</>
	);
};
